import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { setCurrentAccount, isSuperAdmin, isOwner } from '../../lib/auth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { uploadSupportFile, downloadSupportFile, fmtSize } from '../../lib/supportAttachments';
import { LifeBuoy, Loader2, RefreshCw, Search, ExternalLink, Send, StickyNote, Settings2, Paperclip, FileText, X, Download, UserCircle, AlertTriangle, Sparkles, Wand2 } from 'lucide-react';

const STATUS_STYLES = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  waiting_on_customer: 'bg-purple-100 text-purple-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-200 text-slate-600',
};
const PRIORITY_STYLES = {
  urgent: 'bg-red-600 text-white',
  high: 'bg-orange-100 text-orange-700',
  normal: 'bg-slate-100 text-slate-600',
  low: 'bg-slate-50 text-slate-400',
};
const SLA_DOT = { red: 'bg-red-500', amber: 'bg-amber-500', green: 'bg-emerald-500' };
const STATUSES = ['open', 'in_progress', 'waiting_on_customer', 'resolved', 'closed'];

// Built-in one-click canned replies. {name} / {ticket} placeholders filled at click.
const REPLY_TEMPLATES = [
  { key: 'investigating', label: 'Investigating', visibility: 'customer', status: 'in_progress',
    text: "Hi {name},\n\nThanks for reaching out — we've received your request ({ticket}) and are looking into it now. We'll follow up shortly with an update.\n\nThe BillPortal Support Team" },
  { key: 'need_info', label: 'Need more info', visibility: 'customer', status: 'waiting_on_customer',
    text: "Hi {name},\n\nThanks for the details. To help us investigate {ticket} further, could you share a bit more (a screenshot or the specific bill/account involved)?\n\nThe BillPortal Support Team" },
  { key: 'working_on_it', label: 'Working on it', visibility: 'customer', status: 'in_progress',
    text: "Hi {name},\n\nQuick update on {ticket}: our team is actively working on this and we'll let you know as soon as it's sorted.\n\nThe BillPortal Support Team" },
  { key: 'resolved', label: 'Resolved', visibility: 'customer', status: 'resolved',
    text: "Hi {name},\n\nGood news — we've resolved the issue in {ticket}. Please take a look and let us know if anything still needs attention. Happy to help further!\n\nThe BillPortal Support Team" },
  { key: 'internal_escalate', label: 'Escalate (internal)', visibility: 'internal', status: '',
    text: "Escalating internally — needs review by the relevant team. Context: " },
];

const lbl = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

export default function SupportRequestsTab() {
  const navigate = useNavigate();
  const canEditSla = isSuperAdmin() || isOwner();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [sla, setSla] = useState({ amber_hours: 0, red_hours: 24 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [agingFilter, setAgingFilter] = useState('');
  const [search, setSearch] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [note, setNote] = useState('');
  const [visibility, setVisibility] = useState('internal'); // default NO (internal)
  const [newStatus, setNewStatus] = useState('');
  const [noteAttachments, setNoteAttachments] = useState([]);
  const [posting, setPosting] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [slaOpen, setSlaOpen] = useState(false);
  const [slaForm, setSlaForm] = useState({ amber_hours: 0, red_hours: 24 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (assigneeFilter !== 'all') params.append('assignee', assigneeFilter);
      if (agingFilter) params.append('aging', agingFilter);
      if (search.trim()) params.append('search', search.trim());
      const { data } = await api.get(`/super-admin/support/tickets?${params.toString()}`);
      setTickets(data.tickets || []);
      setStats(data.stats || {});
      if (data.sla) { setSla(data.sla); setSlaForm(data.sla); }
    } catch (e) {
      toast.error('Failed to load support requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, assigneeFilter, agingFilter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/super-admin/support/assignees').then(({ data }) => setAssignees(data.assignees || [])).catch(() => {});
  }, []);

  const openDetail = async (t) => {
    setActive(t); setDetail(null); setNote(''); setVisibility('internal'); setNewStatus(''); setNoteAttachments([]);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/super-admin/support/tickets/${t.id}`);
      setDetail(data);
      setActive(data.ticket);
    } catch (e) {
      toast.error('Failed to load ticket');
    } finally {
      setDetailLoading(false);
    }
  };

  const goToOrg = (t) => {
    if (!t.account_id) return toast.error('This ticket has no organisation attached');
    setCurrentAccount({ id: t.account_id, organisation_name: t.org_name });
    toast.success(`Switched to ${t.org_name || 'organisation'}`);
    navigate('/dashboard');
  };

  const assign = async (assignee_id) => {
    try {
      const { data } = await api.put(`/super-admin/support/tickets/${active.id}/assign`, { assignee_id: assignee_id || null });
      setActive({ ...active, assignee_id: data.assignee_id, assignee_name: data.assignee_name });
      toast.success(data.assignee_name ? `Assigned to ${data.assignee_name}` : 'Unassigned');
      load();
    } catch (e) {
      toast.error('Failed to assign');
    }
  };

  const postUpdate = async () => {
    if (!note.trim() && !newStatus && noteAttachments.length === 0) return toast.error('Add a note, attachment or change the status');
    setPosting(true);
    try {
      await api.post(`/super-admin/support/tickets/${active.id}/updates`, {
        note: note.trim(), visibility, status: newStatus || undefined, attachments: noteAttachments,
      });
      toast.success(visibility === 'customer' ? 'Update posted — customer notified by email' : 'Internal note added');
      setNote(''); setNewStatus(''); setNoteAttachments([]);
      await openDetail(active); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post update');
    } finally {
      setPosting(false);
    }
  };

  const saveSla = async () => {
    try {
      const { data } = await api.put('/super-admin/support/sla-config', slaForm);
      setSla(data); setSlaOpen(false); toast.success('SLA thresholds updated'); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save SLA settings');
    }
  };

  const uploadNoteFile = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = '';
    for (const f of files) {
      try {
        const meta = await uploadSupportFile(f, active.id);
        setNoteAttachments((p) => [...p, meta]);
      } catch (err) { toast.error(err.message || 'Upload failed'); }
    }
  };

  const applyTemplate = (tpl) => {
    const name = (active?.user_name || '').split(' ')[0] || (active?.user_email || 'there');
    setNote(tpl.text.replace(/{name}/g, name).replace(/{ticket}/g, active?.ticket_no || 'your ticket'));
    setVisibility(tpl.visibility);
    setNewStatus(tpl.status || '');
  };

  const astridDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/super-admin/support/tickets/${active.id}/draft-reply`);
      setNote(data.draft || '');
      setVisibility('customer');
      toast.success('Astrid drafted a reply — review & edit before sending');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate a draft');
    } finally {
      setDrafting(false);
    }
  };

  const StatCard = ({ label, value, cls, testid }) => (
    <div className={`border p-3 ${cls}`} data-testid={testid}>
      <div className="text-2xl font-black leading-none">{value ?? 0}</div>
      <div className="text-xs mt-1 font-medium opacity-80">{label}</div>
    </div>
  );

  return (
    <div data-testid="support-requests-tab">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <LifeBuoy className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-bold">Support Requests</h2>
            <p className="text-sm text-muted-foreground">Manage tickets raised by organisations across the platform</p>
          </div>
        </div>
        {canEditSla && (
          <Button variant="outline" className="rounded-none" onClick={() => setSlaOpen(true)} data-testid="support-sla-config-btn">
            <Settings2 className="h-4 w-4 mr-2" />SLA Settings
          </Button>
        )}
      </div>

      {/* Top-level SLA stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total tickets" value={stats.total} cls="border-slate-200 bg-white" testid="stat-total" />
        <StatCard label={`Overdue (≥${sla.red_hours}h)`} value={stats.red} cls="border-red-200 bg-red-50 text-red-700" testid="stat-red" />
        <StatCard label="Awaiting reply" value={stats.amber} cls="border-amber-200 bg-amber-50 text-amber-700" testid="stat-amber" />
        <StatCard label="On track / Closed" value={stats.green} cls="border-emerald-200 bg-emerald-50 text-emerald-700" testid="stat-green" />
        <StatCard label="Unassigned" value={stats.unassigned} cls="border-slate-200 bg-slate-50 text-slate-600" testid="stat-unassigned" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input className="rounded-none pl-8 w-56" placeholder="Ticket #, subject, email, org" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="support-admin-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none w-40" data-testid="support-admin-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{lbl(s)} ({stats[s] || 0})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="rounded-none w-44" data-testid="support-admin-assignee-filter"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={agingFilter || 'none'} onValueChange={(v) => setAgingFilter(v === 'none' ? '' : v)}>
          <SelectTrigger className="rounded-none w-52" data-testid="support-admin-aging-filter"><SelectValue placeholder="Ageing" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Any age</SelectItem>
            <SelectItem value="24">Unresponded ≥ 24 hours</SelectItem>
            <SelectItem value="48">Unresponded ≥ 48 hours</SelectItem>
            <SelectItem value="72">Unresponded ≥ 3 days</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-none" onClick={load} data-testid="support-admin-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 text-slate-400" data-testid="support-admin-empty">No support requests found.</div>
      ) : (
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2"></th>
                <th className="px-3 py-2">Ticket #</th>
                <th className="px-3 py-2">Details</th>
                <th className="px-3 py-2">Priority</th>
                <th className="px-3 py-2">Organisation</th>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Registered</th>
                <th className="px-3 py-2">Assignee</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Waiting</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tickets.map((t) => (
                <tr key={t.id} className={`hover:bg-slate-50 cursor-pointer ${t.needs_attention && t.status !== 'closed' && t.status !== 'resolved' ? 'bg-amber-50/60' : ''}`} onClick={() => openDetail(t)} data-testid={`support-admin-row-${t.id}`}>
                  <td className="px-3 py-2"><span className={`inline-block h-2.5 w-2.5 rounded-full ${SLA_DOT[t.sla?.state] || 'bg-slate-300'}`} title={`SLA: ${t.sla?.state}`} /></td>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                    {t.ticket_no}
                    {t.needs_attention && t.status !== 'closed' && t.status !== 'resolved' && <AlertTriangle className="inline h-3 w-3 ml-1 text-amber-600" />}
                  </td>
                  <td className="px-3 py-2 max-w-xs"><div className="font-medium truncate">{t.subject}</div><div className="text-xs text-slate-400">{lbl(t.category)}{(t.attachments||[]).length ? ` · ${(t.attachments||[]).length}📎` : ''}</div></td>
                  <td className="px-3 py-2"><Badge className={`rounded-none ${PRIORITY_STYLES[t.priority] || ''}`}>{lbl(t.priority)}</Badge></td>
                  <td className="px-3 py-2">{t.org_name || '—'}</td>
                  <td className="px-3 py-2 text-xs">{t.user_email}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(t.user_registered_at)}</td>
                  <td className="px-3 py-2 text-xs">{t.assignee_name || <span className="text-slate-300">Unassigned</span>}</td>
                  <td className="px-3 py-2"><Badge className={`rounded-none ${STATUS_STYLES[t.status] || ''}`}>{lbl(t.status)}</Badge></td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{t.sla?.awaiting ? `${t.sla.age_hours}h` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Sheet */}
      <Sheet open={!!active && !!detail} onOpenChange={(o) => { if (!o) { setActive(null); setDetail(null); } }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="support-admin-detail">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><span className="font-mono text-sm text-slate-500">{active?.ticket_no}</span></SheetTitle>
          </SheetHeader>
          {active && detail && (
            <div className="mt-4 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={`rounded-none ${STATUS_STYLES[active.status] || ''}`}>{lbl(active.status)}</Badge>
                  <Badge className={`rounded-none ${PRIORITY_STYLES[active.priority] || ''}`}>{lbl(active.priority)}</Badge>
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${SLA_DOT[active.sla?.state] || 'bg-slate-300'}`} />
                  {active.sla?.awaiting && <span className="text-xs text-slate-500">waiting {active.sla.age_hours}h</span>}
                </div>
                <h3 className="font-bold text-base">{active.subject}</h3>
                <div className="text-xs text-slate-500 mt-1">{active.user_name} · {active.user_email}</div>
                <div className="text-xs text-slate-400">Org: {active.org_name || '—'} · Registered {fmtDate(active.user_registered_at)} · Raised {fmt(active.created_at)}</div>
              </div>

              {/* Assignee */}
              <div className="flex items-center gap-2">
                <UserCircle className="h-4 w-4 text-slate-400" />
                <Select value={active.assignee_id || 'unassigned'} onValueChange={(v) => assign(v === 'unassigned' ? '' : v)}>
                  <SelectTrigger className="rounded-none w-full" data-testid="support-assign-select"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" className="rounded-none w-full" onClick={() => goToOrg(active)} data-testid="support-goto-org"><ExternalLink className="h-4 w-4 mr-2" />Go to Org</Button>

              {active.description && <p className="text-sm text-slate-700 whitespace-pre-wrap border border-slate-200 p-3 bg-slate-50">{active.description}</p>}
              <AdminAttachmentList attachments={active.attachments} />

              <div>
                <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-2">History</h4>
                {(detail.updates || []).length === 0 ? (
                  <p className="text-sm text-slate-400">No updates yet.</p>
                ) : (
                  <div className="space-y-2" data-testid="support-admin-updates">
                    {detail.updates.map((u) => (
                      <div key={u.id} className={`border p-3 text-sm ${u.visibility === 'internal' ? 'border-amber-200 bg-amber-50' : (u.author_role === 'customer' ? 'border-slate-200 bg-slate-50' : 'border-primary/20 bg-primary/5')}`}>
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                          <span className="font-semibold">{u.author_name || u.author_role} {u.visibility === 'internal' && <Badge variant="outline" className="rounded-none ml-1 text-[10px]">Internal</Badge>}{u.type === 'user_reply' && <Badge variant="outline" className="rounded-none ml-1 text-[10px]">Customer</Badge>}</span>
                          <span>{fmt(u.created_at)}</span>
                        </div>
                        {u.note && <p className="whitespace-pre-wrap">{u.note}</p>}
                        <AdminAttachmentList attachments={u.attachments} />
                        {u.status_to && <div className="text-xs text-slate-500 mt-1">→ {lbl(u.status_to)}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-slate-200 p-3 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Add update</Label>
                  <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={astridDraft} disabled={drafting} data-testid="support-astrid-draft-btn">
                    {drafting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5 text-primary" />}
                    Astrid: draft a reply
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="support-reply-templates">
                  {REPLY_TEMPLATES.map((tpl) => (
                    <button key={tpl.key} type="button" onClick={() => applyTemplate(tpl)}
                      className="inline-flex items-center gap-1 border border-slate-200 px-2 py-1 text-xs bg-white hover:bg-slate-50 hover:border-primary/40 transition-colors"
                      data-testid={`support-template-${tpl.key}`}>
                      <Wand2 className="h-3 w-3 text-slate-400" />{tpl.label}
                    </button>
                  ))}
                </div>
                <Textarea className="rounded-none min-h-[90px]" placeholder="Type an update or internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="support-admin-note" />
                {noteAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {noteAttachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-1.5 border border-slate-200 px-2 py-1 text-xs bg-slate-50">
                        <FileText className="h-3 w-3 text-slate-400" /><span className="max-w-[120px] truncate">{a.filename}</span>
                        <button onClick={() => setNoteAttachments(noteAttachments.filter((_, j) => j !== i))}><X className="h-3 w-3 text-slate-400 hover:text-red-600" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer hover:text-slate-900" data-testid="support-admin-attach">
                    <Paperclip className="h-3.5 w-3.5" /> Attach
                    <input type="file" accept="image/png,image/jpeg,application/pdf" multiple className="hidden" onChange={uploadNoteFile} />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={visibility} onValueChange={setVisibility}>
                    <SelectTrigger className="rounded-none" data-testid="support-admin-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Internal note (default)</SelectItem>
                      <SelectItem value="customer">Customer-facing (emails user)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={newStatus || 'nochange'} onValueChange={(v) => setNewStatus(v === 'nochange' ? '' : v)}>
                    <SelectTrigger className="rounded-none" data-testid="support-admin-set-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nochange">Keep status</SelectItem>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{lbl(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button className="rounded-none w-full" onClick={postUpdate} disabled={posting} data-testid="support-admin-post">
                  {posting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Posting…</> : (visibility === 'customer' ? <><Send className="h-4 w-4 mr-2" />Post &amp; notify</> : <><StickyNote className="h-4 w-4 mr-2" />Add internal note</>)}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* SLA config modal */}
      <Dialog open={slaOpen} onOpenChange={setSlaOpen}>
        <DialogContent data-testid="support-sla-modal">
          <DialogHeader><DialogTitle>SLA colour thresholds</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">A ticket turns <span className="text-amber-600 font-medium">Amber</span> once it is awaiting a reply, and <span className="text-red-600 font-medium">Red</span> after the hours below. Responded/closed tickets are <span className="text-emerald-600 font-medium">Green</span>.</p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Amber after (hours awaiting)</Label>
              <Input type="number" min="0" className="rounded-none mt-1" value={slaForm.amber_hours} onChange={(e) => setSlaForm({ ...slaForm, amber_hours: Number(e.target.value) })} data-testid="sla-amber-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Red after (hours awaiting)</Label>
              <Input type="number" min="1" className="rounded-none mt-1" value={slaForm.red_hours} onChange={(e) => setSlaForm({ ...slaForm, red_hours: Number(e.target.value) })} data-testid="sla-red-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setSlaOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveSla} data-testid="sla-save-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminAttachmentList({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((a, i) => (
        <button key={i} type="button" onClick={() => downloadSupportFile(a)} className="flex items-center gap-1.5 border border-slate-200 px-2 py-1 text-xs bg-white hover:bg-slate-50" data-testid={`support-admin-attachment-${a.key}`}>
          <FileText className="h-3.5 w-3.5 text-primary" /><span className="max-w-[140px] truncate">{a.filename}</span>
          <span className="text-slate-400">{fmtSize(a.size)}</span><Download className="h-3 w-3 text-slate-400" />
        </button>
      ))}
    </div>
  );
}
